import type { MarketProduct } from "../types";
import { filterPanel } from "./panel";
import { productFamily } from "./rate-catalog";
import { matchYbrLender } from "./ybr-panel";
import { cleanPrintLabel } from "../lib/printLabel";
import type { VerifiedLedger } from "../server/verify/types";
import ledgerJson from "./verified-rates.json" with { type: "json" };

export { productFamily };

function panelName(lender: string): string {
  return matchYbrLender(lender)?.name ?? lender;
}

function withPanelName(row: MarketProduct): MarketProduct {
  const lender = panelName(row.lender);
  return lender === row.lender ? row : { ...row, lender };
}

function isPackagePrint(product: string): boolean {
  return /offset|advantage|package|professional|wealth/i.test(product);
}

function sameBand(a: MarketProduct, b: MarketProduct): boolean {
  if (a.maxLvr !== b.maxLvr) return false;
  if (a.minLvr == null || b.minLvr == null) return true;
  return Math.abs(a.minLvr - b.minLvr) <= 1;
}

function bandKey(row: MarketProduct): string {
  return `${row.lender}|${row.occupancy}|${row.repayment}|${row.maxLvr}`;
}

function cheaper(a: MarketProduct, b: MarketProduct): MarketProduct {
  if (a.rate < b.rate) return a;
  if (b.rate < a.rate) return b;
  if (a.comparisonRate <= b.comparisonRate) return a;
  return b;
}

function cheapestInBand(rows: MarketProduct[]): MarketProduct[] {
  const map = new Map<string, MarketProduct>();
  for (const row of rows) {
    const key = bandKey(row);
    const prev = map.get(key);
    map.set(key, prev ? cheaper(prev, row) : row);
  }
  return [...map.values()];
}

/**
 * Keep locked prints, refresh them from the live feed, and add a cheaper
 * panel lender when the live feed actually has one. Do not pad a favourite trio.
 */
export function overlayVerifiedCatalogue(
  verified: MarketProduct[],
  live: MarketProduct[],
): MarketProduct[] {
  const locked = filterPanel(verified.map(withPanelName).map((row) => ({ ...row, product: cleanPrintLabel(row.product) })));
  const incoming = cheapestInBand(
    filterPanel(live.map(withPanelName))
      .map((row) => ({ ...row, product: cleanPrintLabel(row.product) }))
      .filter((row) => !isPackagePrint(row.product)),
  );

  const refreshed = locked.map((row) => {
    const hits = incoming.filter(
      (item) =>
        item.lender === row.lender &&
        item.occupancy === row.occupancy &&
        item.repayment === row.repayment &&
        sameBand(row, item) &&
        productFamily(item.product) === productFamily(row.product),
    );
    if (!hits.length) return row;
    const hit = hits.reduce(cheaper);
    return {
      ...row,
      product: row.product,
      rate: hit.rate,
      comparisonRate: hit.comparisonRate,
      source: "cdr" as const,
    };
  });

  const lockedKeys = new Set(refreshed.map(bandKey));
  const extras = incoming.filter(
    (row) =>
      row.minLvr != null &&
      !lockedKeys.has(bandKey(row)) &&
      !refreshed.some((item) => item.lender === row.lender && item.occupancy === row.occupancy && item.repayment === row.repayment && sameBand(item, row)),
  );

  return filterPanel([...refreshed, ...extras]);
}

const ledger = ledgerJson as VerifiedLedger;

export const MARKET_AS_AT = ledger.asAt;

export const LENDERS = [
  "CBA",
  "Westpac",
  "NAB",
  "ANZ",
  "Macquarie",
  "ING",
  "Suncorp",
  "AMP",
  "St George",
  "Bankwest",
  "IMB",
  "ME Bank",
  "UBank",
  "Other",
] as const;

function toProduct(row: VerifiedLedger["products"][number]): MarketProduct {
  return {
    lender: row.lender,
    product: cleanPrintLabel(row.product),
    rate: row.rate,
    comparisonRate: row.comparisonRate,
    occupancy: row.occupancy,
    repayment: row.repayment,
    maxLvr: row.maxLvr,
    minLvr: row.minLvr,
    minLoan: row.minLoan,
    cashback: row.cashback,
    ongoingFee: row.ongoingFee,
    source: row.sourceKind === "cdr" ? "cdr" : "snapshot",
    finePrint: row.finePrint ? cleanPrintLabel(row.finePrint) : row.finePrint,
    ratesUrl: row.ratesUrl,
    verifiedAt: row.verifiedAt,
    sourceKind: row.sourceKind,
    reviewStatus: row.reviewStatus,
  };
}

export const MARKET: MarketProduct[] = filterPanel(
  ledger.products.filter((row) => row.status === "verified").map(toProduct),
);

export function snapshotPayload(): import("../types").MarketPayload {
  const review = ledger.warnings.length;
  return {
    asAt: ledger.asAt,
    asAtIso: ledger.asAtIso,
    source: "snapshot",
    brands: new Set(MARKET.map((p) => p.lender)).size,
    products: MARKET,
    note: review
      ? `Verified lender prints as at ${ledger.asAt}. ${review} item(s) need a page re-check.`
      : `Verified lender prints as at ${ledger.asAt}. Each row cites the lender’s rates page.`,
  };
}
